import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Truck, Plus, Pencil } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CarrierRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ carrier_name: '', carrier_code: '', priority: 1, max_weight_kg: 50, serviceable_pincodes: [], fallback_carrier: '', is_active: true, rate_per_kg: 0 });
  const [pinInput, setPinInput] = useState('');

  useEffect(() => { fetchRules(); }, []);

  const fetchRules = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/carrier-rules`, { withCredentials: true });
      setRules(data.rules || []);
    } catch { toast.error('Failed to load carrier rules'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.carrier_name || !form.carrier_code) { toast.error('Name and code required'); return; }
    try {
      if (editing) {
        await axios.patch(`${API}/api/seller/carrier-rules/${editing.rule_id}`, form, { withCredentials: true });
        toast.success('Carrier updated');
      } else {
        await axios.post(`${API}/api/seller/carrier-rules`, form, { withCredentials: true });
        toast.success('Carrier added');
      }
      setDialogOpen(false);
      setEditing(null);
      fetchRules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const addPincodes = () => {
    const pins = pinInput.split(',').map(p => p.trim()).filter(Boolean);
    setForm(f => ({ ...f, serviceable_pincodes: [...new Set([...f.serviceable_pincodes, ...pins])] }));
    setPinInput('');
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl" data-testid="carrier-rules-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Carrier Rules</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Configure carriers, priorities, and fallback logic</p>
          </div>
          <Button data-testid="add-carrier-btn" onClick={() => { setEditing(null); setForm({ carrier_name: '', carrier_code: '', priority: 1, max_weight_kg: 50, serviceable_pincodes: [], fallback_carrier: '', is_active: true, rate_per_kg: 0 }); setDialogOpen(true); }} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <Plus size={16} className="mr-2" /> Add Carrier
          </Button>
        </div>

        {loading ? <div className="text-center py-12 text-[#9c9db7]">Loading...</div> : rules.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
            <Truck size={48} className="mx-auto text-[#9c9db7] mb-3" />
            <p className="text-[#6b6b8a]">No carrier rules configured.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map((r, i) => (
              <div key={r.rule_id} className={`bg-white rounded-xl border p-5 ${r.is_active ? 'border-[#e5e5ea]' : 'border-red-200 opacity-60'}`} data-testid={`carrier-${r.rule_id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#7d64ff]/10 flex items-center justify-center text-sm font-bold text-[#7d64ff]">{r.priority}</div>
                    <div>
                      <h3 className="font-semibold text-[#0A0A0A]">{r.carrier_name} <span className="text-xs font-mono text-[#9c9db7]">{r.carrier_code}</span></h3>
                      <div className="flex items-center gap-3 mt-1 text-xs text-[#6b6b8a]">
                        <span>Max: {r.max_weight_kg}kg</span>
                        <span>Rate: &#8377;{r.rate_per_kg}/kg</span>
                        <span>{(r.serviceable_pincodes || []).length} pincodes</span>
                        {r.fallback_carrier && <span>Fallback: {r.fallback_carrier}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{r.is_active ? 'Active' : 'Inactive'}</span>
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(r); setForm({ carrier_name: r.carrier_name, carrier_code: r.carrier_code, priority: r.priority, max_weight_kg: r.max_weight_kg, serviceable_pincodes: r.serviceable_pincodes || [], fallback_carrier: r.fallback_carrier || '', is_active: r.is_active, rate_per_kg: r.rate_per_kg }); setDialogOpen(true); }}><Pencil size={14} /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md" data-testid="carrier-dialog">
            <DialogHeader><DialogTitle>{editing ? 'Edit Carrier' : 'Add Carrier'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input data-testid="carrier-name" value={form.carrier_name} onChange={e => setForm(f => ({ ...f, carrier_name: e.target.value }))} placeholder="BlueDart" /></div>
                <div><Label>Code</Label><Input value={form.carrier_code} onChange={e => setForm(f => ({ ...f, carrier_code: e.target.value }))} placeholder="BD" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Priority</Label><Input type="number" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 1 }))} /></div>
                <div><Label>Max Wt (kg)</Label><Input type="number" value={form.max_weight_kg} onChange={e => setForm(f => ({ ...f, max_weight_kg: parseFloat(e.target.value) || 50 }))} /></div>
                <div><Label>Rate/kg</Label><Input type="number" value={form.rate_per_kg} onChange={e => setForm(f => ({ ...f, rate_per_kg: parseFloat(e.target.value) || 0 }))} /></div>
              </div>
              <div><Label>Fallback Carrier Code</Label><Input value={form.fallback_carrier} onChange={e => setForm(f => ({ ...f, fallback_carrier: e.target.value }))} placeholder="Optional" /></div>
              <div><Label>Serviceable Pincodes ({form.serviceable_pincodes.length})</Label>
                <div className="flex gap-2"><Input value={pinInput} onChange={e => setPinInput(e.target.value)} placeholder="110001, 110002, ..." /><Button variant="outline" size="sm" onClick={addPincodes}>Add</Button></div>
                {form.serviceable_pincodes.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{form.serviceable_pincodes.slice(0, 20).map(p => <span key={p} className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">{p}</span>)}{form.serviceable_pincodes.length > 20 && <span className="text-xs text-[#9c9db7]">+{form.serviceable_pincodes.length - 20} more</span>}</div>}
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" /> Active</label>
              <Button data-testid="save-carrier-btn" onClick={handleSave} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef]">{editing ? 'Update' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
